import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toApiUserLevelMetricFilter } from "../../../domain/mappers/metricsMappers";
import { toUserSession } from "../../../domain/mappers/sessionMappers";
import type { UserSession } from "../../../domain/models/session";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../hooks/constants";
import type { UserSessionEntry } from "./types";

export const USER_SESSION_HISTORY_QUERY_KEY = "userSessionHistory";

interface UseUserSessionHistoryParams {
  startDate: Date;
  endDate: Date;
  minSessionDurationInMinutes?: number;
  enabled?: boolean;
}

interface UseUserSessionHistoryResult {
  data: UserSessionEntry[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

const EMPTY_USER_SESSION_HISTORY: UserSessionEntry[] = [];

const toUserSessionEntry = (session: UserSession): UserSessionEntry => ({
  id: `${session.userId}-${session.sessionStartTime.toISOString()}`,
  date: session.sessionStartTime,
  userId: session.userId,
  experienceName: session.experienceName,
  durationSeconds: session.sessionDurationInSeconds,
  country: session.country,
  multiplayerSessionId: session.multiplayerSessionId,
});

export const useUserSessionHistory = ({
  startDate,
  endDate,
  minSessionDurationInMinutes,
  enabled: externalEnabled = true,
}: UseUserSessionHistoryParams): UseUserSessionHistoryResult => {
  const { companyId } = useParams();
  const sessionManagementService = useSessionManagementServiceV1();

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      USER_SESSION_HISTORY_QUERY_KEY,
      companyId,
      startDate.toISOString(),
      endDate.toISOString(),
      minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const filter = toApiUserLevelMetricFilter({
        startDate,
        endDate,
        minSessionDurationInMinutes,
      });

      const result = await sessionManagementService.getUserLevelMetrics(
        companyId,
        filter,
      );

      return result.userSessionDataPoints
        .map(toUserSession)
        .map(toUserSessionEntry);
    },
    enabled: Boolean(companyId && externalEnabled),
    placeholderData: (previousData) => previousData,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
  });

  return {
    data: data ?? EMPTY_USER_SESSION_HISTORY,
    isLoading,
    isFetching,
    error: error ?? null,
  };
};
